import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BandwidthStore } from '../stores/bandwidth.store';
import { bufferTime, catchError, filter, map, Observable, of, takeWhile, tap } from 'rxjs';
import { BandwidthResult } from '../../models/app/bandwidth-result';
import { MapPipe } from '../../shared/pipes/map.pipe';

@Injectable({
  providedIn: 'root',
})
export class BandwidthService {
  private readonly mapPipe = inject(MapPipe);
  private readonly http = inject(HttpClient);
  readonly store = inject(BandwidthStore);

  private readonly UPLOAD_URL = '/api/v1/bandwidth';
  private readonly DOWNLOAD_URL = '/api/v1/bandwidth';

  testDownload(sizeInMb = 5): Observable<BandwidthResult> {
    console.log('Starting download bandwidth test');
    const start = performance.now();
    const expectedBytes = sizeInMb * 1024 * 1024;

    this.init('download');

    return this.http
      .get(this.DOWNLOAD_URL + `?sizeInMb=${sizeInMb}`, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        filter(
          (event) =>
            event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.Response,
        ),
        map((event) => this.buildBandwidthResultFromEvent('download', start, event, expectedBytes)),
        catchError(() =>
          of({
            type: 'download',
            sizeInBytes: 0,
            durationMs: 0,
            bytesPerSecond: 0,
            timestamp: new Date(),
            status: 'error',
          } as BandwidthResult),
        ),
        filter((e) => e.type === 'download'),
        bufferTime(250),
        map((buffered) => this.averageBandwidth(buffered)),
        filter((r): r is BandwidthResult => r !== null),
        tap((result) => this.store.update(result)),
        map(this.computeBandwidthMos.bind(this)),
      );
  }

  testUpload(sizeInMb = 5): Observable<BandwidthResult> {
    console.log('Starting upload bandwidth test');
    const payload = new Uint8Array(sizeInMb * 1024 * 1024);
    const start = performance.now();

    this.init('upload');

    return this.http
      .post(this.UPLOAD_URL, payload.buffer, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        filter(
          (event) =>
            event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response,
        ),
        map((event) =>
          this.buildBandwidthResultFromEvent('upload', start, event, payload.byteLength),
        ),
        catchError(() =>
          of({
            type: 'upload',
            sizeInBytes: 0,
            durationMs: 0,
            bytesPerSecond: 0,
            timestamp: new Date(),
            status: 'error',
          } as BandwidthResult),
        ),
        filter((e) => e.type === 'upload'),
        bufferTime(250),
        map((buffered) => this.averageBandwidth(buffered)),
        filter((r): r is BandwidthResult => r !== null),
        takeWhile(
          () =>
            this.store.snapshot?.status !== 'success' && this.store.snapshot?.status !== 'error',
        ),
        tap((result) => this.store.update(result)),
        map(this.computeBandwidthMos.bind(this)),
      ) as Observable<BandwidthResult>;
  }

  private computeBandwidth(bytes: number, ms: number): number {
    const seconds = ms / 1000;
    return seconds > 0 ? bytes / seconds : 0;
  }

  private buildBandwidthResultFromEvent(
    type: 'upload' | 'download',
    start: number,
    event: HttpEvent<any>,
    expectedTotal?: number,
  ): BandwidthResult {
    const now = performance.now();
    const durationMs = Math.max(now - start, 1); // éviter 0ms

    const loaded = this.getLoadedBytes(event, expectedTotal);

    return {
      type,
      sizeInBytes: loaded,
      durationMs: Math.round(durationMs),
      bytesPerSecond: this.computeBandwidth(loaded, durationMs),
      timestamp: new Date(),
      status: event.type === HttpEventType.Response ? 'success' : 'in_progress',
    };
  }

  private averageBandwidth(results: BandwidthResult[]): BandwidthResult | null {
    if (!results.length) return null;

    const latest = results[results.length - 1]; // on garde les infos contextuelles
    const totalBytes = results.reduce((acc, r) => acc + r.sizeInBytes, 0);
    const totalTime = results.reduce((acc, r) => acc + r.durationMs, 0);

    const avgBytesPerSecond = this.computeBandwidth(totalBytes, totalTime);

    return {
      ...latest,
      bytesPerSecond: avgBytesPerSecond,
    };
  }

  private getLoadedBytes(event: HttpEvent<any>, expectedTotal?: number): number {
    switch (event.type) {
      case HttpEventType.UploadProgress:
        return event.loaded ?? 0;
      case HttpEventType.DownloadProgress:
        return event.loaded ?? 0;

      case HttpEventType.Response:
        if (event.body instanceof Blob) {
          return event.body.size;
        }
        return expectedTotal ?? 0;

      default:
        return 0;
    }
  }

  private init(type: 'upload' | 'download') {
    this.store.update({
      type: type,
      status: 'in_progress',
      bytesPerSecond: 0,
      durationMs: 0,
      sizeInBytes: 0,
      timestamp: new Date(),
    });
  }

  /**
   * Computes MOS (1–5) for bandwidth usage based on type (upload / download).
   * Uses bitrate computed from size and duration.
   *
   * Upload: maps from 25–50 kbps to MOS 1–5
   * Download (Full HD): maps from 1500–3000 kbps to MOS 1–5
   *
   * @param result - The BandwidthResult object to update.
   * @returns The same BandwidthResult with mos populated.
   */
  private computeBandwidthMos(result: BandwidthResult): BandwidthResult {
    // Convert bytes/ms to kbps
    const bps = (result.sizeInBytes * 8) / (result.durationMs / 1000);
    const kbps = bps / 1000;

    let mos: number;

    if (result.type === 'upload') {
      // Audio-only upstream
      mos = this.mapPipe.transform(kbps, 25, 50, 1, 5);
      console.log(`Upload MOS: ${mos} for ${kbps} kbps`);
    } else {
      // Download: Full HD video
      mos = this.mapPipe.transform(kbps, 1500, 3000, 1, 5);
      console.log(`Download MOS: ${mos} for ${kbps} kbps`);
    }

    // Clamp MOS between 1 and 5
    result.mos = Math.max(1, Math.min(5, parseFloat(mos.toFixed(2))));
    result.bytesPerSecond = bps;

    return result;
  }
}
